import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

type CalendarDay = {
  date: Date;
  inMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isDisabled: boolean;
};

@Component({
  selector: 'app-date-time-picker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateTimePickerComponent),
      multi: true,
    },
  ],
  host: {
    class: 'block',
    '(document:click)': 'onDocumentClick($event)',
  },
  templateUrl: './date-time-picker.component.html',
  styleUrl: './date-time-picker.component.css',
})
export class DateTimePickerComponent implements ControlValueAccessor {
  readonly id = input<string | null>(null);
  readonly placeholder = input('Select date & time');
  readonly required = input(false);
  readonly min = input<string | null>(null);
  readonly max = input<string | null>(null);
  readonly disabled = input(false);
  readonly ariaLabel = input<string | null>(null);
  readonly describedBy = input<string | null>(null);

  protected readonly open = signal(false);
  protected readonly dateValue = signal<string | null>(null);
  protected readonly timeValue = signal<string>('');
  protected readonly viewDate = signal(this.startOfDay(this.today()));

  private readonly controlDisabled = signal(false);
  protected readonly isDisabled = computed(() => this.disabled() || this.controlDisabled());
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly displayFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  private readonly monthFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
  });

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  protected readonly panelId = computed(() => `${this.id() ?? 'date-time-picker'}-panel`);
  protected readonly triggerId = computed(() => this.id() ?? 'date-time-picker');
  protected readonly selectedDate = computed(() => this.parseDate(this.dateValue()));
  protected readonly displayValue = computed(() => {
    const date = this.selectedDate();
    const time = this.timeValue();
    if (!date || !time) {
      return '';
    }
    const value = this.combineDateTime(date, time);
    return value ? this.displayFormatter.format(value) : '';
  });
  protected readonly monthLabel = computed(() => this.monthFormatter.format(this.viewDate()));
  protected readonly weekdayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  protected readonly weeks = computed(() => {
    const viewDate = this.viewDate();
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const startOffset = firstOfMonth.getDay();
    const gridStart = new Date(year, month, 1 - startOffset);
    const days: CalendarDay[] = [];
    const selected = this.selectedDate();
    const today = this.startOfDay(this.today());
    const minDate = this.parseDate(this.min());
    const maxDate = this.parseDate(this.max());

    for (let i = 0; i < 42; i += 1) {
      const date = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
      const inMonth = date.getMonth() === month;
      const isToday = this.isSameDay(date, today);
      const isSelected = selected ? this.isSameDay(date, selected) : false;
      const isDisabled = this.isOutsideBounds(date, minDate, maxDate) || this.isDisabled();
      days.push({ date, inMonth, isToday, isSelected, isDisabled });
    }

    const weeks: CalendarDay[][] = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    return weeks;
  });

  writeValue(value: string | Date | null): void {
    if (typeof value === 'string') {
      const { date, time } = this.splitDateTime(value);
      this.dateValue.set(date);
      this.timeValue.set(time ?? '');
      return;
    }

    if (value instanceof Date) {
      this.dateValue.set(this.formatDateInput(value));
      this.timeValue.set(this.formatTimeInput(value));
      return;
    }

    this.dateValue.set(null);
    this.timeValue.set('');
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.controlDisabled.set(isDisabled);
    if (isDisabled) {
      this.closePanel();
    }
  }

  protected togglePanel(): void {
    if (this.isDisabled()) {
      return;
    }

    if (this.open()) {
      this.closePanel();
      return;
    }

    this.openPanel();
  }

  protected openPanel(): void {
    if (this.isDisabled()) {
      return;
    }

    const selected = this.selectedDate();
    this.viewDate.set(this.startOfDay(selected ?? this.today()));
    if (!this.timeValue()) {
      this.timeValue.set(this.formatTimeInput(this.now()));
    }
    this.open.set(true);
  }

  protected closePanel(): void {
    if (!this.open()) {
      return;
    }

    this.open.set(false);
    this.onTouched();
  }

  protected focusPreviousMonth(): void {
    const view = this.viewDate();
    this.viewDate.set(new Date(view.getFullYear(), view.getMonth() - 1, 1));
  }

  protected focusNextMonth(): void {
    const view = this.viewDate();
    this.viewDate.set(new Date(view.getFullYear(), view.getMonth() + 1, 1));
  }

  protected selectDay(day: CalendarDay, event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (day.isDisabled) {
      return;
    }

    this.dateValue.set(this.formatDateInput(day.date));
    this.emitValue();
  }

  protected updateTime(value: string): void {
    this.timeValue.set(value);
    this.emitValue();
  }

  protected clearDate(event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.dateValue.set(null);
    this.timeValue.set('');
    this.onChange(null);
    this.closePanel();
  }

  protected handleTriggerKeydown(event: KeyboardEvent): void {
    if (this.isDisabled()) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closePanel();
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.togglePanel();
    }
  }

  protected onDocumentClick(event: MouseEvent): void {
    if (!this.open()) {
      return;
    }

    const target = event.target as Node | null;
    if (target && !this.elementRef.nativeElement.contains(target)) {
      this.closePanel();
    }
  }

  private emitValue(): void {
    const date = this.selectedDate();
    const time = this.timeValue();
    if (!date || !time) {
      this.onChange(null);
      return;
    }

    const combined = this.combineDateTime(date, time);
    this.onChange(combined ? this.formatDateTimeInput(combined) : null);
  }

  private splitDateTime(value: string | null): { date: string | null; time: string | null } {
    if (!value) {
      return { date: null, time: null };
    }

    const [date, time] = value.split('T');
    if (!date) {
      return { date: null, time: null };
    }

    return { date, time: time?.slice(0, 5) ?? null };
  }

  private combineDateTime(date: Date, time: string): Date | null {
    const [hours, minutes] = time.split(':').map((part) => Number(part));
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return null;
    }

    const value = new Date(date);
    value.setHours(hours, minutes, 0, 0);
    return value;
  }

  private formatDateInput(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatTimeInput(date: Date): string {
    const hours = `${date.getHours()}`.padStart(2, '0');
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private formatDateTimeInput(date: Date): string {
    return `${this.formatDateInput(date)}T${this.formatTimeInput(date)}`;
  }

  private parseDate(value: string | null): Date | null {
    if (!value) {
      return null;
    }

    const parts = value.split('-').map((part) => Number(part));
    if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
      return null;
    }

    const [year, month, day] = parts;
    return this.startOfDay(new Date(year, month - 1, day));
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private now(): Date {
    return new Date();
  }

  private today(): Date {
    return this.startOfDay(this.now());
  }

  private isSameDay(left: Date, right: Date): boolean {
    return (
      left.getFullYear() === right.getFullYear() &&
      left.getMonth() === right.getMonth() &&
      left.getDate() === right.getDate()
    );
  }

  private isOutsideBounds(date: Date, min: Date | null, max: Date | null): boolean {
    if (min && date < min) {
      return true;
    }

    if (max && date > max) {
      return true;
    }

    return false;
  }
}
